/**
 * TIM SDK 核心模块
 * 职责：SDK 实例创建、初始化、登录登出
 */
import TencentCloudChat from '@tencentcloud/chat'
import TIMUploadPlugin from 'tim-upload-plugin'
import { eventBus, SDK_EVENTS } from '../eventBus.js'
import { SDK_ERRORS } from '@/errors/codes.js'
import { handleSDKError } from '@/errors/handler.js'
import { logger } from '@/utils/logger.js'
import { ENV } from '@/config/index.js'

const log = logger.module('TIM')

// SDK 实例（模块级单例）
let timInstance = null

/**
 * 创建并初始化 TIM SDK
 * @param {Object} credentials - 登录凭证
 * @param {string} credentials.sdkAppId
 * @param {string} credentials.userId
 * @param {string} credentials.userSig
 * @returns {Promise<TencentCloudChat>}
 */
export async function createTIM(credentials) {
  const { sdkAppId, userId, userSig } = credentials

  // 防止重复初始化
  if (timInstance) {
    return timInstance
  }

  // 校验参数
  if (!sdkAppId || !userId || !userSig) {
    const err = handleSDKError(null, SDK_ERRORS.TIM_LOGIN_FAILED, credentials)
    throw err
  }

  try {
    // 创建 SDK 实例
    timInstance = TencentCloudChat.create({
      SDKAppID: Number(sdkAppId)
    })

    // 设置日志级别（生产环境降低）
    timInstance.setLogLevel(ENV.isProd ? 2 : 1)

    // 注册文件上传插件
    timInstance.registerPlugin({ 'tim-upload-plugin': TIMUploadPlugin })

    // 注册 SDK 事件监听
    _registerSDKEvents(timInstance)

    // 执行登录
    await timInstance.login({ userID: userId, userSig })

    log.info('SDK initialized, loginUser:', timInstance.getLoginUser())

    return timInstance
  } catch (error) {
    const err = handleSDKError(error, SDK_ERRORS.TIM_LOGIN_FAILED, { userId })
    throw err
  }
}

/**
 * 注册 SDK 事件
 * @param {TencentCloudChat} tim
 */
function _registerSDKEvents(tim) {
  const Event = TencentCloudChat.EVENT

  // SDK 就绪
  tim.on(Event.SDK_READY, () => {
    log.info('SDK ready')
    localStorage.setItem('h5im_sdk_ready', '1')
    eventBus.emit(SDK_EVENTS.SDK_READY)
  })

  // SDK 未就绪
  tim.on(Event.SDK_NOT_READY, () => {
    log.warn('SDK not ready')
    localStorage.removeItem('h5im_sdk_ready')
    eventBus.emit(SDK_EVENTS.SDK_NOT_READY)
  })

  // 被踢出
  tim.on(Event.KICKED_OUT, (event) => {
    log.warn('Kicked out:', event.data)
    localStorage.removeItem('h5im_sdk_ready')
    eventBus.emit(SDK_EVENTS.KICKED_OUT, event.data)
  })

  // SDK 错误
  tim.on(Event.ERROR, (event) => {
    log.error('SDK error:', event.data)
    eventBus.emit(SDK_EVENTS.TIM_ERROR, event.data)
  })
}

/**
 * 获取 TIM SDK 实例
 * @returns {TencentCloudChat|null}
 */
export function getTIM() {
  return timInstance
}

/**
 * 检查 SDK 是否已初始化
 * @returns {boolean}
 */
export function isTIMReady() {
  return timInstance !== null && localStorage.getItem('h5im_sdk_ready') === '1'
}

/**
 * 获取当前登录用户 ID
 * @returns {string|null}
 */
export function getLoginUser() {
  if (!timInstance) return null
  try {
    return timInstance.getLoginUser() || null
  } catch {
    return null
  }
}

/**
 * 登出并销毁 SDK
 * @returns {Promise<void>}
 */
export async function destroyTIM() {
  if (!timInstance) return

  try {
    await timInstance.logout()
    timInstance.destroy()
    log.info('SDK destroyed')
  } catch (error) {
    log.warn('Logout error:', error.message)
  }

  timInstance = null
  localStorage.removeItem('h5im_sdk_ready')
  // 只移除 TIM 相关的事件监听，保留 TRTC 和业务事件的监听
  eventBus.removeAllListeners(SDK_EVENTS.SDK_READY)
  eventBus.removeAllListeners(SDK_EVENTS.SDK_NOT_READY)
  eventBus.removeAllListeners(SDK_EVENTS.KICKED_OUT)
  eventBus.removeAllListeners(SDK_EVENTS.TIM_ERROR)
  eventBus.removeAllListeners(SDK_EVENTS.MESSAGE_RECEIVED)
  eventBus.removeAllListeners(SDK_EVENTS.CONVERSATION_LIST_UPDATED)
  eventBus.removeAllListeners(SDK_EVENTS.MESSAGE_READ_BY_PEER)
  eventBus.removeAllListeners(SDK_EVENTS.MESSAGE_REVOKED)
  eventBus.removeAllListeners(SDK_EVENTS.CALL_SIGNAL)
}

export default {
  createTIM,
  getTIM,
  isTIMReady,
  getLoginUser,
  destroyTIM
}
